import React, { useState, useEffect, useMemo } from 'react';
import { message, Input, DatePicker, Button, Table, Modal, Space, Badge, Select } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import dayjs from 'dayjs';
import { StyledCard } from '../common/StyledCard';
import { FormLabel } from '../common/FormLabel';
import { theme } from '../../theme';
import { EditOutlined, PlusOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;

// Definir el tipo para ImportMeta
interface ImportMetaEnv {
  readonly REACT_APP_INSUMOS_URL: string
}

interface ImportMeta {
  readonly env: ImportMetaEnv
}

interface ITransportista {
  id: number;
  nc: string;
  denominacion: string;
}

interface IChofer {
  id: number;
  nombre: string;
  cuit: string;
  numero_carnet: string;
  contrato_art: string;
  vencimiento_carnet: string;
  vencimiento_art: string;
}

const TransportistaChoferes: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  
  // Estados para los selects
  const [transportistas, setTransportistas] = useState<ITransportista[]>([]);
  const [transportistaSeleccionado, setTransportistaSeleccionado] = useState<number | null>(null);
  const [choferes, setChoferes] = useState<IChofer[]>([]);

  // Estados para la búsqueda de transportista
  const [searchTransportista, setSearchTransportista] = useState('');
  const [showSearchTransportista, setShowSearchTransportista] = useState(false);
  const [selectedTransportista, setSelectedTransportista] = useState<ITransportista | null>(null);

  // Estados para el modal
  const [modalVisible, setModalVisible] = useState(false);
  const [choferEditando, setChoferEditando] = useState<IChofer | null>(null);
  const [isGrabando, setIsGrabando] = useState(false);

  // Estados para los inputs del formulario
  const [nombreChofer, setNombreChofer] = useState('');
  const [cuitChofer, setCuitChofer] = useState('');
  const [carnetChofer, setCarnetChofer] = useState('');
  const [contratoArt, setContratoArt] = useState('');
  const [vencimientoCarnet, setVencimientoCarnet] = useState<dayjs.Dayjs | null>(null);
  const [vencimientoArt, setVencimientoArt] = useState<dayjs.Dayjs | null>(null);
  const [carnetModificadoManual, setCarnetModificadoManual] = useState(false);

  // Estado para el filtro
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');

  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;

  // Agregar estado de carga inicial
  const [isLoading, setIsLoading] = useState(true);

  // Cargar transportistas al iniciar y cuando cambie id_empresa
  useEffect(() => {
    const fetchTransportistas = async () => {
      if (!id_empresa) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const token = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Error al cargar transportistas');
        }

        const data = await response.json();
        setTransportistas(data);
      } catch (error) {
        console.error('Error:', error);
        message.error('Error al cargar los transportistas');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransportistas();
  }, [getAccessTokenSilently, id_empresa]);

  const handleTransportistaChange = async (transportistaId: number) => {
    setTransportistaSeleccionado(transportistaId);
    setChoferes([]);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/chofer/${id_empresa}/${transportistaId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar choferes');
      }

      const data = await response.json();
      setChoferes(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los choferes');
    }
  };

  const abrirModalNuevo = () => {
    setChoferEditando(null);
    limpiarFormulario();
    setCarnetModificadoManual(false);
    setModalVisible(true);
  };

  const abrirModalEditar = (chofer: IChofer) => {
    setChoferEditando(chofer);
    setNombreChofer(chofer.nombre || '');
    setCuitChofer(chofer.cuit || '');
    setCarnetChofer(chofer.numero_carnet || '');
    setContratoArt(chofer.contrato_art || '');
    setVencimientoCarnet(chofer.vencimiento_carnet ? dayjs(chofer.vencimiento_carnet) : null);
    setVencimientoArt(chofer.vencimiento_art ? dayjs(chofer.vencimiento_art) : null);
    setCarnetModificadoManual(true); // En edición, no sincronizar automáticamente
    setModalVisible(true);
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setChoferEditando(null);
    limpiarFormulario();
  };

  const limpiarFormulario = () => {
    setNombreChofer('');
    setCuitChofer('');
    setCarnetChofer('');
    setContratoArt('');
    setVencimientoCarnet(null);
    setVencimientoArt(null);
    setCarnetModificadoManual(false);
  };

  const validarFormulario = () => {
    return (
      nombreChofer.trim() !== '' &&
      cuitChofer.trim() !== '' &&
      carnetChofer.trim() !== '' &&
      contratoArt.trim() !== '' &&
      vencimientoCarnet !== null &&
      vencimientoArt !== null
    );
  };

  const handleGuardar = async () => {
    if (!validarFormulario()) {
      message.warning('Por favor complete todos los campos requeridos');
      return;
    }

    try {
      setIsGrabando(true);
      const token = await getAccessTokenSilently();

      const datosChofer = {
        nombre: nombreChofer,
        cuit: cuitChofer,
        carnet: carnetChofer,
        contrato_art: contratoArt,
        vencimiento_carnet: vencimientoCarnet?.format('YYYY-MM-DD'),
        vencimiento_art: vencimientoArt?.format('YYYY-MM-DD')
      };

      let response;
      if (choferEditando) {
        // Modificar
        response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/editar_relacion_transportista_chofer/${id_empresa}/${transportistaSeleccionado}/${choferEditando.id}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(datosChofer)
          }
        );
      } else {
        // Crear
        response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/crear_relacion_transportista_chofer/${id_empresa}/${transportistaSeleccionado}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(datosChofer)
          }
        );
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al guardar los datos');
      }

      message.success(choferEditando ? 'Chofer modificado correctamente' : 'Chofer creado correctamente');
      cerrarModal();
      
      // Recargar lista de choferes
      if (transportistaSeleccionado) {
        handleTransportistaChange(transportistaSeleccionado);
      }
    } catch (error: any) {
      console.error('Error:', error);
      message.error(error.message || 'Error al guardar los datos');
    } finally {
      setIsGrabando(false);
    }
  };

  // Estilos
  const selectContainerStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px'
  };

  // Efecto para cerrar el dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.transportista-search-container')) {
        setShowSearchTransportista(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para determinar el estado del ART
  const obtenerEstadoART = (fechaVencimiento: string | null): 'vigente' | 'proximo' | 'vencido' => {
    if (!fechaVencimiento) return 'vencido';
    
    const fecha = dayjs(fechaVencimiento);
    const hoy = dayjs();
    const diasHastaVencimiento = fecha.diff(hoy, 'day');

    if (diasHastaVencimiento < 0) {
      return 'vencido';
    } else if (diasHastaVencimiento <= 30) {
      return 'proximo';
    } else {
      return 'vigente';
    }
  };

  // Filtrar choferes según el estado seleccionado
  const choferesFiltrados = useMemo(() => {
    if (filtroEstado === 'todos') {
      return choferes;
    }
    return choferes.filter(chofer => {
      const estado = obtenerEstadoART(chofer.vencimiento_art);
      return estado === filtroEstado;
    });
  }, [choferes, filtroEstado]);

  // Contar choferes vencidos
  const choferesVencidos = useMemo(() => {
    return choferes.filter(chofer => obtenerEstadoART(chofer.vencimiento_art) === 'vencido').length;
  }, [choferes]);

  // Columnas de la tabla
  const columns: ColumnsType<IChofer> = [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 200,
    },
    {
      title: 'CUIT',
      dataIndex: 'cuit',
      key: 'cuit',
      width: 120,
    },
    {
      title: 'Carnet',
      dataIndex: 'numero_carnet',
      key: 'numero_carnet',
      width: 120,
    },
    {
      title: 'Vencimiento Carnet',
      dataIndex: 'vencimiento_carnet',
      key: 'vencimiento_carnet',
      width: 150,
      render: (text: string) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Contrato ART',
      dataIndex: 'contrato_art',
      key: 'contrato_art',
      width: 120,
    },
    {
      title: 'Vencimiento ART',
      dataIndex: 'vencimiento_art',
      key: 'vencimiento_art',
      width: 150,
      render: (text: string) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Estado ART',
      key: 'estado_art',
      width: 120,
      render: (_: any, record: IChofer) => {
        const estado = obtenerEstadoART(record.vencimiento_art);
        const config = {
          vigente: { color: 'success', text: 'Vigente' },
          proximo: { color: 'warning', text: 'Próximo' },
          vencido: { color: 'error', text: 'Vencido' },
        };
        const estadoConfig = config[estado];
        return (
          <Badge 
            status={estadoConfig.color as any} 
            text={estadoConfig.text}
            style={{ fontSize: '12px' }}
          />
        );
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: IChofer) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => abrirModalEditar(record)}
          size="small"
        >
          Editar
        </Button>
      ),
    },
  ];

  return (
    <div>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px', fontSize: theme.fontSizes.normal }}>
          Cargando...
        </div>
      ) : (
        <>
          <StyledCard bodyStyle={{ padding: '12px' }}>
            <div style={selectContainerStyle}>
              {/* Select Transportista con búsqueda */}
              <div style={{ flex: 1 }}>
                <FormLabel required>Transportista</FormLabel>
                <div className="transportista-search-container" style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="text"
                    placeholder="Buscar por NC o denominación..."
                    required
                    style={{
                      width: '100%',
                      paddingLeft: '30px',
                      paddingRight: selectedTransportista ? '30px' : undefined,
                      paddingTop: '6px',
                      paddingBottom: '6px',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.sm,
                      marginBottom: '4px',
                      backgroundColor: selectedTransportista ? theme.colors.backgroundGray : 'white',
                      cursor: 'pointer',
                      height: '32px',
                      fontSize: '13px'
                    }}
                    value={selectedTransportista ? `${selectedTransportista.nc} - ${selectedTransportista.denominacion}` : searchTransportista}
                    onChange={e => {
                      setSearchTransportista(e.target.value);
                      setSelectedTransportista(null);
                      setTransportistaSeleccionado(null);
                      setShowSearchTransportista(true);
                    }}
                    onFocus={() => setShowSearchTransportista(true)}
                  />
                  {/* Icono de búsqueda */}
                  <SearchOutlined style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.colors.textSecondary,
                    pointerEvents: 'none',
                    fontSize: '14px'
                  }} />
                  {/* Limpiar selección */}
                  {selectedTransportista && (
                    <CloseOutlined
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: theme.colors.textSecondary,
                        cursor: 'pointer',
                        fontSize: '14px',
                        zIndex: 1100
                      }}
                      onClick={() => {
                        setSelectedTransportista(null);
                        setSearchTransportista('');
                        setTransportistaSeleccionado(null);
                        setChoferes([]);
                        setShowSearchTransportista(true);
                      }}
                      title="Limpiar selección"
                    />
                  )}
                  {/* Dropdown */}
                  {showSearchTransportista && transportistas.length > 0 && !selectedTransportista && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.sm,
                      maxHeight: '180px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: theme.shadows.md
                    }}>
                      {transportistas.filter(t => {
                        const search = searchTransportista.trim().toLowerCase();
                        return (
                          t.nc.toLowerCase().includes(search) ||
                          t.denominacion.toLowerCase().includes(search)
                        );
                      }).map(transportista => (
                        <div
                          key={transportista.id}
                          style={{
                            padding: '6px 12px',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${theme.colors.borderLight}`,
                            textAlign: 'left',
                            fontSize: '13px'
                          }}
                          onMouseOver={e => (e.currentTarget.style.backgroundColor = theme.colors.backgroundGray)}
                          onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                          onClick={() => {
                            setSelectedTransportista(transportista);
                            setTransportistaSeleccionado(transportista.id);
                            setShowSearchTransportista(false);
                            handleTransportistaChange(transportista.id);
                          }}
                        >
                          {transportista.nc} - {transportista.denominacion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Botón Nuevo Chofer */}
              <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '4px' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={abrirModalNuevo}
                  disabled={!transportistaSeleccionado}
                  size="small"
                  style={{ height: '32px', fontSize: '12px' }}
                >
                  Nuevo Chofer
                </Button>
              </div>
            </div>

            {/* Filtro y Tabla de choferes */}
            {transportistaSeleccionado && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FormLabel style={{ marginBottom: 0 }}>Filtrar por estado ART:</FormLabel>
                    <Select
                      value={filtroEstado}
                      onChange={setFiltroEstado}
                      size="small"
                      style={{ width: 180, fontSize: '12px' }}
                    >
                      <Option value="todos">Todos</Option>
                      <Option value="vigente">Vigentes</Option>
                      <Option value="proximo">Próximos a vencer</Option>
                      <Option value="vencido">Vencidos</Option>
                    </Select>
                  </div>
                  {choferesVencidos > 0 && (
                    <Badge 
                      count={choferesVencidos} 
                      style={{ backgroundColor: theme.colors.error }}
                    >
                      <span style={{ fontSize: '12px', color: theme.colors.error, fontWeight: 500 }}>
                        Contratos ART vencidos
                      </span>
                    </Badge>
                  )}
                </div>
                <Table
                  scroll={{ x: 'max-content' }}
                  columns={columns}
                  dataSource={choferesFiltrados}
                  rowKey="id"
                  size="small"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} choferes`,
                  }}
                  locale={{
                    emptyText: filtroEstado === 'todos' 
                      ? 'No hay choferes registrados para este transportista'
                      : `No hay choferes con estado "${filtroEstado}"`
                  }}
                />
              </>
            )}
          </StyledCard>

          {/* Modal para crear/editar chofer */}
          <Modal
            title={choferEditando ? 'Editar Chofer' : 'Nuevo Chofer'}
            open={modalVisible}
            onCancel={cerrarModal}
            onOk={handleGuardar}
            confirmLoading={isGrabando}
            okText="Guardar"
            cancelText="Cancelar"
            width={600}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '12px' }}>
              <div>
                <FormLabel required>Nombre</FormLabel>
                <Input
                  value={nombreChofer}
                  onChange={(e) => setNombreChofer(e.target.value)}
                  placeholder="Ingrese nombre del chofer"
                  style={{ height: '32px', fontSize: '13px' }}
                  maxLength={40}
                />
              </div>

              <div>
                <FormLabel required>CUIT</FormLabel>
                <Input
                  value={cuitChofer}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setCuitChofer(value);
                    // Si no está editando y el carnet no fue modificado manualmente, sincronizar con CUIT
                    // Pero limitar a 10 caracteres (tamaño máximo del campo en BD)
                    if (!choferEditando && !carnetModificadoManual) {
                      setCarnetChofer(value.slice(0, 10));
                    }
                  }}
                  placeholder="Ingrese CUIT"
                  style={{ height: '32px', fontSize: '13px' }}
                  maxLength={11}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <FormLabel required>Carnet</FormLabel>
                  <Input
                    value={carnetChofer}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 10); // Limitar a 10 caracteres
                      setCarnetChofer(value);
                      setCarnetModificadoManual(true); // Marcar como modificado manualmente
                    }}
                    placeholder="Ingrese número de carnet"
                    style={{ height: '32px', fontSize: '13px' }}
                    maxLength={10}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <FormLabel required>Vencimiento Carnet</FormLabel>
                  <DatePicker
                    value={vencimientoCarnet}
                    onChange={(date) => setVencimientoCarnet(date)}
                    format="DD/MM/YYYY"
                    size="small"
                    style={{ width: '100%', height: '32px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <FormLabel required>Contrato ART</FormLabel>
                  <Input
                    value={contratoArt}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 10); // Limitar a 10 caracteres
                      setContratoArt(value);
                    }}
                    placeholder="Ingrese contrato ART"
                    style={{ height: '32px', fontSize: '13px' }}
                    maxLength={10}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <FormLabel required>Vencimiento ART</FormLabel>
                  <DatePicker
                    value={vencimientoArt}
                    onChange={(date) => setVencimientoArt(date)}
                    format="DD/MM/YYYY"
                    size="small"
                    style={{ width: '100%', height: '32px' }}
                  />
                </div>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default TransportistaChoferes;
